import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isMarketSettled, isOutcomeSettled, useMarketRealtime } from '@/lib/realtime';
import { invalidateTradingCachesDebounced } from './invalidate-trading-caches';

/** Resync retries after a settlement signal — covers Plaee's ~1–2s settle lag. */
const RESYNC_DELAYS_MS = [3000, 6000] as const;

/**
 * Refetches positions/orders/trades when a market settles, so a resolved position
 * leaves Open Positions and shows in My History (Won/Lost), and any resting orders
 * the settlement cancels drop off. Settlement emits no fill/trade, so it rides the
 * public market channel (reusing useMarketRealtime) instead of
 * {@link useTradingRealtimeSync} or the backend-only `position:updated`. The
 * invalidation is debounced+shared, so N markets settling together (and the retry
 * waves) collapse to one refetch cycle each.
 */
export function useSettlementResync(marketId: string | null | undefined, externalMarketId: string | null | undefined): void {
  const queryClient = useQueryClient();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const resync = useCallback(() => invalidateTradingCachesDebounced(queryClient), [queryClient]);

  const scheduleResync = useCallback(() => {
    timersRef.current.forEach(clearTimeout); // drop pending so repeated signals don't stack
    timersRef.current = [];
    resync();
    for (const delay of RESYNC_DELAYS_MS) timersRef.current.push(setTimeout(resync, delay));
  }, [resync]);

  // Clear timers on unmount and on market change (page stays mounted across switches).
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [marketId, externalMarketId],
  );

  useMarketRealtime(marketId, externalMarketId, {
    onOutcomeUpdated: (e) => {
      if (isOutcomeSettled(e)) scheduleResync();
    },
    onMarketUpdated: (e) => {
      if (isMarketSettled(e)) scheduleResync();
    },
  });
}
